/**
 * Decode the `exp` claim from a JWT (without verifying its signature — this
 * is purely for proactive-refresh scheduling on the client). Returns the
 * expiry as a millisecond epoch, or `null` if the token can't be parsed.
 */
export function getTokenExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]!);
    const payload = JSON.parse(json) as { exp?: number | undefined};
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Decode the `sub` claim from a JWT — the canonical user id. Used by the
 * itinerary-store scope manager to derive which storage namespace the
 * persisted blob lives under. No signature verification (client-side
 * scoping is not a security boundary; the server still enforces per-user
 * access on every request).
 */
export function getTokenSub(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]!);
    const payload = JSON.parse(json) as { sub?: unknown };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

/** Base64url → utf-8 string. Works on web, native, and node. */
function base64UrlDecode(input: string): string {
  // Replace URL-safe chars and pad with `=` to a multiple of 4.
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');

  if (typeof globalThis.atob === 'function') {
    // Browsers (and modern RN via the URL polyfill) ship atob.
    const binary = globalThis.atob(padded);
    return binaryToUtf8(binary);
  }

  // Node fallback (used by jest before any RN polyfills load).
  const buf =
    typeof globalThis.Buffer === 'function'
      ? globalThis.Buffer.from(padded, 'base64')
      : null;
  if (buf) return buf.toString('utf8');

  // Last-resort manual decode (rare).
  return manualBase64Decode(padded);
}

function binaryToUtf8(binary: string): string {
  // Convert a binary-string-encoded UTF-8 byte sequence into a real JS string.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

function manualBase64Decode(input: string): string {
  const lookup =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const stripped = input.replace(/=+$/, '');
  let buffer = 0;
  let bits = 0;
  let out = '';
  for (let i = 0; i < stripped.length; i++) {
    const v = lookup.indexOf(stripped[i]!);
    if (v === -1) continue;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >> bits) & 0xff);
    }
  }
  return out;
}
